import { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, Check, Search, UserRound } from 'lucide-react';
import { CleviaLogo } from '@/components/CleviaLogo';
import { FIELD_CLASS, FIELD_WITH_ICON_CLASS, HeroBackdrop } from '@/components/landing/page-kit';
import { getBrandLogoUrl } from '@/lib/brands';
import { getCountryFlagUrl } from '@/lib/banks';

interface OnboardingData {
  firstName: string;
  dateOfBirth: string;
  gender: string;
  country: string;
  lifeSituation: string;
  lifeSituationOther: string;
  managementStyle: string;
  challenges: string[];
  challengeOther: string;
  expectations: string[];
  discoverySource: string;
  discoveryOther: string;
  referralCode: string;
  email: string;
  password: string;
}

interface CountryOption {
  code: string;
  name: string;
  flagUrl?: string;
}

interface FirstCountriesResponse {
  data?: Record<string, { country?: string }>;
}

type StepType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const FEATURED_COUNTRIES: CountryOption[] = [
  { code: 'MA', name: 'Maroc' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Espagne' },
  { code: 'BE', name: 'Belgique' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'CA', name: 'Canada' },
];

const ALL_COUNTRIES = [
  ...FEATURED_COUNTRIES,
  { code: 'CH', name: 'Suisse' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'IT', name: 'Italie' },
  { code: 'PT', name: 'Portugal' },
  { code: 'US', name: 'États-Unis' },
  { code: 'NL', name: 'Pays-Bas' },
  { code: 'TN', name: 'Tunisie' },
  { code: 'DZ', name: 'Algérie' },
  { code: 'SN', name: 'Sénégal' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'AT', name: 'Autriche' },
  { code: 'IE', name: 'Irlande' },
  { code: 'SE', name: 'Suède' },
];

const LIFE_SITUATIONS = [
  { id: 'student', icon: '🎓' },
  { id: 'alternant', icon: '📚' },
  { id: 'young_active', icon: '🚀' },
  { id: 'employee', icon: '💼' },
  { id: 'entrepreneur', icon: '🏗️' },
  { id: 'no_income', icon: '🔄' },
  { id: 'other', icon: '💡' },
];

const LIFE_SITUATION_LABELS: Record<string, { fr: string; en: string }> = {
  student: { fr: 'Étudiant(e)', en: 'Student' },
  alternant: { fr: 'Alternant(e)', en: 'Work-study' },
  young_active: { fr: 'Jeune actif / premier emploi', en: 'Young professional / first job' },
  employee: { fr: 'Salarié(e)', en: 'Employee' },
  entrepreneur: { fr: 'Entrepreneur / freelance', en: 'Entrepreneur / freelance' },
  no_income: { fr: 'Sans revenu fixe / en transition', en: 'No fixed income / in transition' },
  other: { fr: 'Autre', en: 'Other' },
};

const MANAGEMENT_STYLES = [
  { id: 'solo', icon: '👤' },
  { id: 'couple', icon: '👫' },
  { id: 'family', icon: '🏠' },
];

const MANAGEMENT_LABELS: Record<string, { fr: string; en: string }> = {
  solo: { fr: 'Seul(e)', en: 'Alone' },
  couple: { fr: 'En couple', en: 'As a couple' },
  family: { fr: 'En famille / foyer', en: 'Family / household' },
};

const GENDER_OPTIONS = [
  { id: 'male', icon: '♂' },
  { id: 'female', icon: '♀' },
  { id: 'prefer_not_to_say', icon: '' },
] as const;

const GENDER_LABELS: Record<string, { fr: string; en: string }> = {
  male: { fr: 'Homme', en: 'Man' },
  female: { fr: 'Femme', en: 'Woman' },
  prefer_not_to_say: { fr: 'Je préfère ne pas le dire', en: 'Prefer not to say' },
};

const CHALLENGE_OPTIONS = [
  { id: 'track_expenses', icon: '📊' },
  { id: 'save_regularly', icon: '🐷' },
  { id: 'where_money_goes', icon: '🤷' },
  { id: 'manage_couple', icon: '👫' },
  { id: 'manage_debts', icon: '💳' },
  { id: 'track_investments', icon: '📈' },
  { id: 'other', icon: '💡' },
];

const CHALLENGE_LABELS: Record<string, { fr: string; en: string }> = {
  track_expenses: {
    fr: "Je n'arrive pas à suivre mes dépenses",
    en: "I can't keep track of my expenses",
  },
  save_regularly: {
    fr: "J'ai du mal à épargner régulièrement",
    en: 'I struggle to save regularly',
  },
  where_money_goes: {
    fr: 'Je ne sais pas vraiment où part mon argent',
    en: "I don't really know where my money goes",
  },
  manage_couple: {
    fr: 'Je veux mieux gérer à deux',
    en: 'I want to manage finances better as a couple',
  },
  manage_debts: {
    fr: 'Je veux mieux gérer mes crédits / dettes',
    en: 'I want to manage my credits / debts better',
  },
  track_investments: {
    fr: 'Je veux mieux suivre mes investissements',
    en: 'I want to better track my investments',
  },
  other: { fr: 'Autre', en: 'Other' },
};

const EXPECTATION_OPTIONS = [
  { id: 'budget_clarity', icon: '👁️' },
  { id: 'prepare_project', icon: '🎯' },
  { id: 'safety_savings', icon: '🛡️' },
  { id: 'household_finance', icon: '🏠' },
  { id: 'track_patrimoine', icon: '📈' },
  { id: 'money_basics', icon: '🌱' },
];

const EXPECTATION_LABELS: Record<string, { fr: string; en: string }> = {
  budget_clarity: {
    fr: 'Voir clair sur mon budget au quotidien',
    en: 'See my daily budget clearly',
  },
  prepare_project: {
    fr: 'Préparer un projet (voyage, voiture, logement…)',
    en: 'Prepare a project (travel, car, housing…)',
  },
  safety_savings: { fr: 'Construire une épargne de sécurité', en: 'Build an emergency fund' },
  household_finance: {
    fr: 'Mieux gérer les finances de mon foyer',
    en: 'Better manage my household finances',
  },
  track_patrimoine: {
    fr: 'Suivre mon patrimoine et mes investissements',
    en: 'Track my assets and investments',
  },
  money_basics: {
    fr: 'Poser des bases saines pour mon argent (je débute)',
    en: "Build solid money foundations (I'm just starting)",
  },
};

const DISCOVERY_SOURCES = [
  { id: 'facebook', domain: 'facebook.com' },
  { id: 'instagram', domain: 'instagram.com' },
  { id: 'x', domain: 'x.com' },
  { id: 'youtube', domain: 'youtube.com' },
  { id: 'tiktok', domain: 'tiktok.com' },
  { id: 'linkedin', domain: 'linkedin.com' },
  { id: 'google', domain: 'google.com' },
  { id: 'friends', domain: '' },
  { id: 'other', domain: '' },
];

const DISCOVERY_LABELS: Record<string, { fr: string; en: string }> = {
  facebook: { fr: 'Facebook', en: 'Facebook' },
  instagram: { fr: 'Instagram', en: 'Instagram' },
  x: { fr: 'X', en: 'X' },
  youtube: { fr: 'YouTube', en: 'YouTube' },
  tiktok: { fr: 'TikTok', en: 'TikTok' },
  linkedin: { fr: 'LinkedIn', en: 'LinkedIn' },
  google: { fr: 'Recherche Google', en: 'Google Search' },
  friends: { fr: 'Amis ou famille', en: 'Friends or family' },
  other: { fr: 'Autre', en: 'Other' },
};

export default function NewRegisterPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language } = useTranslation();
  const { isAuthenticated, onboardingComplete, user, refreshOnboarding } = useAuth();

  const [currentStep, setCurrentStep] = useState<StepType>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchCountry, setSearchCountry] = useState('');
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [countries, setCountries] = useState<CountryOption[]>(ALL_COUNTRIES);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [countriesError, setCountriesError] = useState(false);
  const [dobInput, setDobInput] = useState('');
  const [dobError, setDobError] = useState('');

  const [formData, setFormData] = useState<OnboardingData>({
    firstName: '',
    dateOfBirth: '',
    gender: '',
    country: '',
    lifeSituation: '',
    lifeSituationOther: '',
    managementStyle: '',
    challenges: [],
    challengeOther: '',
    expectations: [],
    discoverySource: '',
    discoveryOther: '',
    referralCode: '',
    email: '',
    password: '',
  });

  // Pre-fill firstName from user metadata for existing users
  useEffect(() => {
    if (isAuthenticated && user?.name && !formData.firstName) {
      const first = user.name.split(' ')[0];
      if (first) setFormData((prev) => ({ ...prev, firstName: first }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.name]);

  // Parse dd/mm/yyyy input into yyyy-mm-dd for formData
  useEffect(() => {
    const match = dobInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, dd, mm, yyyy] = match;
      const day = parseInt(dd, 10);
      const month = parseInt(mm, 10);
      const year = parseInt(yyyy, 10);
      const date = new Date(year, month - 1, day);
      if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
        const today = new Date();
        let age = today.getFullYear() - year;
        const m = today.getMonth() - (month - 1);
        if (m < 0 || (m === 0 && today.getDate() < day)) age--;
        if (age > 100) {
          setDobError(language === 'fr' ? 'Âge maximum : 100 ans' : 'Maximum age: 100 years');
          setFormData((prev) => ({ ...prev, dateOfBirth: '' }));
        } else {
          setDobError('');
          setFormData((prev) => ({ ...prev, dateOfBirth: `${yyyy}-${mm}-${dd}` }));
        }
      } else {
        setDobError(language === 'fr' ? 'Date invalide' : 'Invalid date');
        setFormData((prev) => ({ ...prev, dateOfBirth: '' }));
      }
    } else {
      if (dobInput.length === 10) {
        setDobError(
          language === 'fr' ? 'Format attendu : jj/mm/aaaa' : 'Expected format: dd/mm/yyyy'
        );
      } else {
        setDobError('');
      }
      setFormData((prev) => ({ ...prev, dateOfBirth: '' }));
    }
  }, [dobInput, language]);

  useEffect(() => {
    if (!showAllCountries) return;

    const controller = new AbortController();
    setIsLoadingCountries(true);
    setCountriesError(false);

    fetch('https://api.first.org/data/v1/countries', {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to load countries');
        return (await response.json()) as FirstCountriesResponse;
      })
      .then((response) => {
        const displayNames = new Intl.DisplayNames([language === 'fr' ? 'fr' : 'en'], {
          type: 'region',
        });
        const apiCountries = Object.entries(response.data || {})
          .filter(([code]) => code.toUpperCase() !== 'EH')
          .map(([rawCode, country]) => {
            const code = rawCode.toUpperCase();
            return {
              code,
              name: displayNames.of(code) || country.country || code,
              flagUrl: getCountryFlagUrl(code),
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, language === 'fr' ? 'fr' : 'en'));

        setCountries(apiCountries);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setCountriesError(true);
      })
      .finally(() => setIsLoadingCountries(false));

    return () => controller.abort();
  }, [language, showAllCountries]);

  const updateFormData = (field: keyof OnboardingData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleMultiSelect = (field: 'challenges' | 'expectations', id: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(id)
        ? prev[field].filter((v) => v !== id)
        : [...prev[field], id],
    }));
  };

  const displayedCountries = useMemo(() => {
    if (!showAllCountries) return FEATURED_COUNTRIES;
    const normalizedSearch = searchCountry
      .trim()
      .toLocaleLowerCase(language === 'fr' ? 'fr-FR' : 'en-US');
    const filteredCountries = normalizedSearch
      ? countries.filter((country) =>
          country.name
            .toLocaleLowerCase(language === 'fr' ? 'fr-FR' : 'en-US')
            .includes(normalizedSearch)
        )
      : countries;
    const selectedCountry = countries.find((country) => country.code === formData.country);

    if (!selectedCountry) return filteredCountries;

    return [
      selectedCountry,
      ...filteredCountries.filter((country) => country.code !== selectedCountry.code),
    ];
  }, [countries, formData.country, language, searchCountry, showAllCountries]);

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 0: {
        if (!formData.firstName.trim() || !formData.dateOfBirth || !formData.gender) return false;
        const dob = new Date(formData.dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
        return age >= 18;
      }
      case 1:
        return !!formData.country;
      case 2:
        return !!formData.lifeSituation;
      case 3:
        return !!formData.managementStyle;
      case 4:
        return formData.challenges.length > 0;
      case 5:
        return formData.expectations.length > 0;
      case 6:
        return !!formData.discoverySource;
      case 7:
        return true; // referral is optional
      case 8:
        return (
          formData.email &&
          formData.password.length >= 8 &&
          /[a-z]/.test(formData.password) &&
          /[A-Z]/.test(formData.password) &&
          /[0-9]/.test(formData.password)
        );
      default:
        return false;
    }
  }, [currentStep, formData]);

  // Authenticated user who still needs onboarding (Google OAuth OR existing user without onboarding row)
  const needsOnboardingOnly = isAuthenticated && onboardingComplete === false;
  const totalSteps = needsOnboardingOnly ? 8 : 9;

  // Redirect authenticated users who already completed onboarding
  if (isAuthenticated && onboardingComplete === true) {
    return <Navigate to="/dashboard" replace />;
  }

  const saveOnboardingData = async (userId: string) => {
    const { error } = await supabase.from('user_onboarding').insert({
      user_id: userId,
      first_name: formData.firstName,
      age: (() => {
        const dob = new Date(formData.dateOfBirth);
        const today = new Date();
        let a = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) a--;
        return a;
      })(),
      date_of_birth: formData.dateOfBirth,
      gender: formData.gender,
      country_code: formData.country,
      life_situation: formData.lifeSituation,
      life_situation_other: formData.lifeSituationOther || null,
      management_style: formData.managementStyle,
      challenges: formData.challenges,
      challenge_other: formData.challengeOther || null,
      expectations: formData.expectations,
      discovery_source: formData.discoverySource,
      discovery_other: formData.discoveryOther || null,
      referral_code: formData.referralCode || null,
      onboarding_completed_at: new Date().toISOString(),
    });

    if (error) throw error;
  };

  const handleNext = async () => {
    if (!canProceed) return;

    // Authenticated users completing onboarding: save after the last question step (7)
    if (needsOnboardingOnly && currentStep === 7) {
      setIsLoading(true);
      try {
        await saveOnboardingData(user!.id);
        await refreshOnboarding();

        localStorage.setItem('showWelcome', 'true');
        localStorage.setItem('userName', formData.firstName);

        toast({
          title: `🎉 ${t('onboarding.welcome.title')}`,
          description: t('onboarding.welcome.message'),
        });

        navigate('/dashboard', { replace: true });
      } catch (error) {
        toast({
          title: 'Erreur',
          description: error instanceof Error ? error.message : 'Une erreur est survenue',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => (prev + 1) as StepType);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => (prev - 1) as StepType);
    }
  };

  const handleSubmit = async () => {
    if (!canProceed) return;

    setIsLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.firstName,
          },
          emailRedirectTo: window.location.origin,
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Registration failed');

      // Sign out the partial session FIRST (email not confirmed yet)
      // Must happen before writing to localStorage, otherwise
      // onAuthStateChange SIGNED_IN handler would consume the data prematurely
      await supabase.auth.signOut();

      // Now store onboarding data in localStorage — it will be saved to DB
      // when the user confirms their email and a real session is created
      const onboardingPayload = {
        firstName: formData.firstName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        country: formData.country,
        lifeSituation: formData.lifeSituation,
        lifeSituationOther: formData.lifeSituationOther,
        managementStyle: formData.managementStyle,
        challenges: formData.challenges,
        challengeOther: formData.challengeOther,
        expectations: formData.expectations,
        discoverySource: formData.discoverySource,
        discoveryOther: formData.discoveryOther,
        referralCode: formData.referralCode,
      };
      localStorage.setItem('clevia_pending_onboarding', JSON.stringify(onboardingPayload));
      localStorage.setItem('showWelcome', 'true');
      localStorage.setItem('userName', formData.firstName);

      toast({
        title: language === 'fr' ? '📧 Vérifie ta boîte mail' : '📧 Check your inbox',
        description:
          language === 'fr'
            ? 'Un email de confirmation a été envoyé. Clique sur le lien pour activer ton compte.'
            : 'A confirmation email has been sent. Click the link to activate your account.',
      });

      navigate('/login', { replace: true });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Reusable renderer for single-select button lists
  const renderSingleSelect = (
    title: string,
    subtitle: string | null,
    items: { id: string; icon: string }[],
    labelsMap: Record<string, { fr: string; en: string }>,
    selectedValue: string,
    onSelect: (id: string) => void,
    otherField?: { value: string; onChange: (v: string) => void; placeholder: string }
  ) => (
    <div className="space-y-6 animate-slide-up">
      <div>
        <h2 className="mb-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
          {title}
        </h2>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>

      <div className="space-y-3 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onSelect(item.id)}
            className={`flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors duration-200 ${
              selectedValue === item.id
                ? 'border-primary/60 bg-primary/5'
                : 'border-border bg-card hover:border-primary/40 hover:bg-muted/50'
            }`}
          >
            <span className="text-2xl flex-shrink-0">{item.icon}</span>
            <span className="flex-1 text-left text-sm font-medium leading-tight">
              {labelsMap[item.id][language]}
            </span>
            {selectedValue === item.id && <Check className="h-4 w-4 shrink-0 text-primary" />}
          </button>
        ))}
      </div>

      {otherField && selectedValue === 'other' && (
        <Input
          type="text"
          placeholder={otherField.placeholder}
          value={otherField.value}
          onChange={(e) => otherField.onChange(e.target.value)}
          className={FIELD_CLASS}
          autoFocus
        />
      )}
    </div>
  );

  // Reusable renderer for multi-select button lists
  const renderMultiSelect = (
    title: string,
    subtitle: string | null,
    items: { id: string; icon: string }[],
    labelsMap: Record<string, { fr: string; en: string }>,
    selectedValues: string[],
    onToggle: (id: string) => void,
    otherField?: { value: string; onChange: (v: string) => void; placeholder: string }
  ) => (
    <div className="space-y-6 animate-slide-up">
      <div>
        <h2 className="mb-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
          {title}
        </h2>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>

      <div className="space-y-3 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onToggle(item.id)}
            className={`flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors duration-200 ${
              selectedValues.includes(item.id)
                ? 'border-primary/60 bg-primary/5'
                : 'border-border bg-card hover:border-primary/40 hover:bg-muted/50'
            }`}
          >
            <span className="text-2xl flex-shrink-0">{item.icon}</span>
            <span className="flex-1 text-left text-sm font-medium leading-tight">
              {labelsMap[item.id][language]}
            </span>
            {selectedValues.includes(item.id) && (
              <Check className="h-4 w-4 shrink-0 text-primary" />
            )}
          </button>
        ))}
      </div>

      {otherField && selectedValues.includes('other') && (
        <Input
          type="text"
          placeholder={otherField.placeholder}
          value={otherField.value}
          onChange={(e) => otherField.onChange(e.target.value)}
          className={FIELD_CLASS}
          autoFocus
        />
      )}
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      // Step 0: First name + Age
      case 0:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h2 className="mb-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                {t('onboarding.identity.title')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('onboarding.identity.subtitle')}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="firstName" className="mb-2 block text-sm font-medium">
                  {t('onboarding.identity.firstName')}
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder={t('onboarding.identity.firstNamePlaceholder')}
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', e.target.value)}
                  className={`${FIELD_CLASS} h-14 text-base`}
                  autoFocus
                />
              </div>

              <div>
                <Label className="mb-2 block text-sm font-medium">
                  {t('onboarding.dateOfBirth.label')}
                </Label>
                <Input
                  data-testid="dob-input"
                  type="text"
                  inputMode="numeric"
                  placeholder="jj/mm/aaaa"
                  maxLength={10}
                  value={dobInput}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^\d/]/g, '');
                    // Auto-insert slashes
                    const digits = val.replace(/\//g, '');
                    if (digits.length >= 5) {
                      val = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
                    } else if (digits.length >= 3) {
                      val = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                    }
                    setDobInput(val);
                  }}
                  className={`${FIELD_CLASS} h-14 text-base ${dobError ? 'border-destructive focus-visible:ring-destructive/30' : ''}`}
                />
                {dobError && <p className="mt-1.5 text-xs text-destructive">{dobError}</p>}
              </div>

              <div>
                <Label className="mb-2 block text-sm font-medium">
                  {t('onboarding.gender.label')}
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {GENDER_OPTIONS.map(({ id, icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => updateFormData('gender', id)}
                      className={`flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-lg border px-2 py-3 text-center transition-colors duration-200 ${
                        formData.gender === id
                          ? 'border-primary/60 bg-primary/5'
                          : 'border-border bg-card hover:border-primary/40 hover:bg-muted/50'
                      }`}
                    >
                      {icon ? (
                        <span aria-hidden="true" className="text-xl leading-none">
                          {icon}
                        </span>
                      ) : (
                        <UserRound className="h-5 w-5 text-muted-foreground" />
                      )}
                      <span className="text-xs font-medium leading-tight">
                        {GENDER_LABELS[id][language]}
                      </span>
                      {formData.gender === id && <Check className="h-3.5 w-3.5 text-primary" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      // Step 1: Country
      case 1:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h2 className="mb-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                {t('onboarding.country.title')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('onboarding.country.subtitle')}</p>
            </div>

            {showAllCountries && (
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('onboarding.country.search')}
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  className={FIELD_WITH_ICON_CLASS}
                  autoFocus
                />
              </div>
            )}

            <div className="space-y-3 max-h-[380px] overflow-y-auto custom-scrollbar pr-1">
              {isLoadingCountries && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t('onboarding.country.loading')}
                </p>
              )}

              {!isLoadingCountries && countriesError && (
                <p className="py-6 text-center text-sm text-destructive">
                  {t('onboarding.country.error')}
                </p>
              )}

              {!isLoadingCountries && !countriesError && displayedCountries.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t('onboarding.country.noResults')}
                </p>
              )}

              {!isLoadingCountries &&
                !countriesError &&
                displayedCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => {
                      updateFormData('country', country.code);
                      setSearchCountry('');
                    }}
                    className={`flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors duration-200 ${
                      formData.country === country.code
                        ? 'border-primary/60 bg-primary/5'
                        : 'border-border bg-card hover:border-primary/40 hover:bg-muted/50'
                    }`}
                  >
                    <img
                      src={country.flagUrl || getCountryFlagUrl(country.code)}
                      alt={country.name}
                      className="h-8 w-8 shrink-0 rounded-full border border-border object-cover"
                    />
                    <span className="flex-1 text-left text-sm font-medium">{country.name}</span>
                    {formData.country === country.code && (
                      <Check className="h-4 w-4 shrink-0 text-primary" />
                    )}
                  </button>
                ))}

              {!showAllCountries && (
                <button
                  type="button"
                  onClick={() => setShowAllCountries(true)}
                  className="flex w-full items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors duration-200 hover:border-primary/40 hover:bg-muted/50"
                >
                  <span className="text-3xl flex-shrink-0">🌍</span>
                  <span className="flex-1 text-left text-sm font-medium">
                    {t('onboarding.country.other')}
                  </span>
                </button>
              )}
            </div>
          </div>
        );

      // Step 2: Life situation
      case 2:
        return renderSingleSelect(
          t('onboarding.lifeSituation.title'),
          t('onboarding.lifeSituation.subtitle'),
          LIFE_SITUATIONS,
          LIFE_SITUATION_LABELS,
          formData.lifeSituation,
          (id) => updateFormData('lifeSituation', id),
          {
            value: formData.lifeSituationOther,
            onChange: (v) => updateFormData('lifeSituationOther', v),
            placeholder:
              language === 'fr' ? 'Précise ta situation...' : 'Describe your situation...',
          }
        );

      // Step 3: Management style
      case 3:
        return renderSingleSelect(
          t('onboarding.management.title'),
          t('onboarding.management.subtitle'),
          MANAGEMENT_STYLES,
          MANAGEMENT_LABELS,
          formData.managementStyle,
          (id) => updateFormData('managementStyle', id)
        );

      // Step 4: Challenges (multi-select)
      case 4:
        return renderMultiSelect(
          t('onboarding.challenges.title'),
          t('onboarding.challenges.subtitle'),
          CHALLENGE_OPTIONS,
          CHALLENGE_LABELS,
          formData.challenges,
          (id) => toggleMultiSelect('challenges', id),
          {
            value: formData.challengeOther,
            onChange: (v) => updateFormData('challengeOther', v),
            placeholder: language === 'fr' ? 'Précise ton défi...' : 'Describe your challenge...',
          }
        );

      // Step 5: Expectations (multi-select)
      case 5:
        return renderMultiSelect(
          t('onboarding.expectations.title'),
          t('onboarding.expectations.subtitle'),
          EXPECTATION_OPTIONS,
          EXPECTATION_LABELS,
          formData.expectations,
          (id) => toggleMultiSelect('expectations', id)
        );

      // Step 6: Discovery source (with brand logos)
      case 6:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h2 className="mb-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                {t('onboarding.discovery.title')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('onboarding.discovery.subtitle')}</p>
            </div>

            <div className="space-y-3 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
              {DISCOVERY_SOURCES.map((source) => (
                <button
                  key={source.id}
                  onClick={() => updateFormData('discoverySource', source.id)}
                  className={`flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors duration-200 ${
                    formData.discoverySource === source.id
                      ? 'border-primary/60 bg-primary/5'
                      : 'border-border bg-card hover:border-primary/40 hover:bg-muted/50'
                  }`}
                >
                  {source.domain ? (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-white">
                      <img
                        src={getBrandLogoUrl(source.domain, 64)}
                        alt={source.id}
                        className="w-5 h-5 object-contain"
                      />
                    </div>
                  ) : (
                    <span className="text-2xl flex-shrink-0">
                      {source.id === 'friends' ? '👥' : '💡'}
                    </span>
                  )}
                  <span className="flex-1 text-left text-sm font-medium leading-tight">
                    {DISCOVERY_LABELS[source.id][language]}
                  </span>
                  {formData.discoverySource === source.id && (
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </button>
              ))}
            </div>

            {formData.discoverySource === 'other' && (
              <Input
                type="text"
                placeholder={language === 'fr' ? 'Précise...' : 'Tell us more...'}
                value={formData.discoveryOther}
                onChange={(e) => updateFormData('discoveryOther', e.target.value)}
                className={FIELD_CLASS}
                autoFocus
              />
            )}
          </div>
        );

      // Step 7: Referral code
      case 7:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h2 className="mb-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                {t('onboarding.referral.title')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('onboarding.referral.subtitle')}</p>
            </div>

            <div>
              <Label htmlFor="referral" className="mb-2 block text-sm font-medium">
                {t('onboarding.referral.label')}
              </Label>
              <Input
                id="referral"
                type="text"
                placeholder={t('onboarding.referral.placeholder')}
                value={formData.referralCode}
                onChange={(e) => updateFormData('referralCode', e.target.value.toUpperCase())}
                className={`${FIELD_CLASS} h-14 text-center text-base tracking-[0.3em]`}
                maxLength={10}
              />
            </div>

            <p className="text-xs text-muted-foreground">{t('onboarding.referral.hint')}</p>

            <button
              onClick={handleNext}
              className="text-sm font-medium text-brand-700 transition-opacity hover:opacity-80 dark:text-brand-400"
            >
              {t('onboarding.skip')} →
            </button>
          </div>
        );

      // Step 8: Account creation
      case 8:
        return (
          <div className="space-y-6 animate-slide-up">
            <div>
              <h2 className="mb-3 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                {t('onboarding.account.title')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('onboarding.account.subtitle')}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="mb-2 block text-sm font-medium">
                  {t('onboarding.account.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('onboarding.account.emailPlaceholder')}
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className={`${FIELD_CLASS} h-14 text-base`}
                  autoFocus
                />
              </div>

              <div>
                <Label htmlFor="password" className="mb-2 block text-sm font-medium">
                  {t('onboarding.account.password')}
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('onboarding.account.passwordPlaceholder')}
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  className={`${FIELD_CLASS} h-14 text-base`}
                />
                {formData.password.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {[
                      {
                        ok: formData.password.length >= 8,
                        label: language === 'fr' ? '8 caractères minimum' : '8 characters minimum',
                      },
                      {
                        ok: /[a-z]/.test(formData.password),
                        label: language === 'fr' ? 'Une minuscule (a-z)' : 'One lowercase (a-z)',
                      },
                      {
                        ok: /[A-Z]/.test(formData.password),
                        label: language === 'fr' ? 'Une majuscule (A-Z)' : 'One uppercase (A-Z)',
                      },
                      {
                        ok: /[0-9]/.test(formData.password),
                        label: language === 'fr' ? 'Un chiffre (0-9)' : 'One digit (0-9)',
                      },
                    ].map((rule) => (
                      <div key={rule.label} className="flex items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${rule.ok ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                        >
                          {rule.ok && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <span
                          className={`text-xs transition-colors ${rule.ok ? 'text-brand-700 dark:text-brand-400' : 'text-muted-foreground'}`}
                        >
                          {rule.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <p className="pt-2 text-xs text-muted-foreground">
                {t('onboarding.account.terms')}{' '}
                <a href="/p/terms" className="text-brand-700 hover:underline dark:text-brand-400">
                  {t('onboarding.account.termsLink')}
                </a>{' '}
                {t('onboarding.account.and')}{' '}
                <a href="/p/privacy" className="text-brand-700 hover:underline dark:text-brand-400">
                  {t('onboarding.account.privacyLink')}
                </a>{' '}
                {t('onboarding.account.ofclevia')}
              </p>
            </div>

            <div className="pt-4">
              <Button
                onClick={handleSubmit}
                disabled={isLoading || !canProceed}
                className="h-12 w-full shadow-lg shadow-brand-600/20 transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" />
                    {language === 'fr' ? 'Création en cours...' : 'Creating...'}
                  </div>
                ) : (
                  t('onboarding.finish')
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const progressPercentage = ((currentStep + 1) / totalSteps) * 100;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <HeroBackdrop />

      <div className="relative z-10 px-6 py-6">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="transition-opacity duration-200 hover:opacity-80"
          >
            <CleviaLogo size="md" />
          </button>

          <div className="text-sm text-muted-foreground">
            {t('onboarding.step')} {currentStep + 1} {t('onboarding.of')} {totalSteps}
          </div>
        </div>

        <div className="max-w-2xl mx-auto mt-6">
          <div className="h-px overflow-hidden bg-border">
            <div
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      <div className="relative z-10 px-6 py-12 pb-32">
        <div className="max-w-2xl mx-auto">{renderStep()}</div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-background via-background/90 to-transparent px-6 py-6">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          {currentStep > 0 && (
            <Button
              onClick={handleBack}
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('onboarding.previous')}
            </Button>
          )}

          {!isLastStep && (
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className={`ml-auto h-11 px-8 font-medium transition-transform duration-200 ${
                canProceed
                  ? 'shadow-lg shadow-brand-600/20 hover:-translate-y-0.5 active:translate-y-0'
                  : 'cursor-not-allowed bg-muted text-muted-foreground'
              }`}
            >
              {t('onboarding.next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {isLastStep && needsOnboardingOnly && (
            <Button
              onClick={handleNext}
              disabled={!canProceed || isLoading}
              className={`ml-auto h-11 px-8 font-medium transition-transform duration-200 ${
                canProceed
                  ? 'shadow-lg shadow-brand-600/20 hover:-translate-y-0.5 active:translate-y-0'
                  : 'cursor-not-allowed bg-muted text-muted-foreground'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" />
                  {language === 'fr' ? 'Finalisation...' : 'Finishing...'}
                </div>
              ) : (
                t('onboarding.finish')
              )}
            </Button>
          )}
        </div>
      </div>

      {currentStep === 0 && (
        <div className="relative z-10 text-center pb-6">
          <p className="text-sm text-muted-foreground">
            {t('onboarding.hasAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-brand-700 transition-opacity hover:opacity-80 dark:text-brand-400"
            >
              {t('onboarding.login')}
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}
